import { ConnectionState, Target } from "api/client.generated";
import usePartnerConnections from "hooks/api/usePartnerConnections";
import { createContext, useContext, useMemo } from "react";

interface IAuthorizedTargetsContext {
  authorizedTargets: Target[];
}

const AuthorizedTargetsContext = createContext<IAuthorizedTargetsContext | null>(null);

interface AuthorizedTargetsContextProviderProps {
  children: React.ReactNode;
}

export const AuthorizedTargetsContextProvider = ({
  children,
}: AuthorizedTargetsContextProviderProps) => {
  const { partnerConnections, isPartnerConnectionsFetching } = usePartnerConnections();

  const contextValue = useMemo<IAuthorizedTargetsContext>(() => {
    if (isPartnerConnectionsFetching || !partnerConnections) return { authorizedTargets: [] };

    const authorizedTargets: Target[] = [];

    Object.keys(partnerConnections.configuredTargets).forEach((x: Target) => {
      if (partnerConnections.configuredTargets[x] !== ConnectionState.RequestAccess)
        authorizedTargets.push(x);
    });

    return { authorizedTargets };
  }, [partnerConnections, isPartnerConnectionsFetching]);

  return (
    <AuthorizedTargetsContext.Provider value={contextValue}>
      {children}
    </AuthorizedTargetsContext.Provider>
  );
};

export const useAuthorizedTargetsContext = (): IAuthorizedTargetsContext => {
  const context = useContext(AuthorizedTargetsContext);
  if (!context)
    throw new Error(
      "useAuthorizedTargetsContext must be used within a AuthorizedTargetsContextProvider",
    );

  return context;
};
